import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Play, Pause, X, Rewind, FastForward, Loader2, Volume2,
  Timer, Gauge, ChevronDown, Check,
} from 'lucide-react';
import { useAudio } from '../context/AudioContext';

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const SLEEP_OPTIONS = [
  { key: 1,   label: '1 min (Test)' },
  { key: 10,  label: '10 min' },
  { key: 15,  label: '15 min' },
  { key: 30,  label: '30 min' },
  { key: 45,  label: '45 min' },
  { key: 60,  label: '1 hour' },
  { key: 'end_of_track', label: 'End of track' },
];

/**
 * FullPlayerScreen — the full-screen player that overlays the app when
 * the user taps the mini-player. Reads / writes the same central
 * AudioContext, so opening it never restarts audio and every control
 * stays in sync.
 *
 * Content-aware:
 *   - Spoken audio (kind !== 'soundscape'): shows Speed + Sleep Timer.
 *   - Soundscapes (kind === 'soundscape'): shows Sleep Timer only (no
 *     speed — pointless on ambient loops).
 */
const FullPlayerScreen = ({ open, onClose }) => {
  const { state, toggle, skip, seek, setRate, setSleepTimer } = useAudio();
  const { current, playing, loading, position, duration, rate, error, sleepTimer } = state;
  const [showSpeed, setShowSpeed] = useState(false);
  const [showSleep, setShowSleep] = useState(false);
  const [tickTock, setTickTock] = useState(0);

  // Re-render every 30s so the sleep-timer remaining string is fresh.
  useEffect(() => {
    if (!open || !sleepTimer?.endsAt || sleepTimer.endsAt === 'end_of_track') return;
    const i = setInterval(() => setTickTock((n) => n + 1), 30_000);
    return () => clearInterval(i);
  }, [open, sleepTimer]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open || !current) return null;

  const pct = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;
  const isSoundscape = current?.kind === 'soundscape';

  const sleepRemainingLabel = (() => {
    if (!sleepTimer) return null;
    if (sleepTimer.endsAt === 'end_of_track') return 'End of track';
    const minutes = Math.max(0, Math.ceil((sleepTimer.endsAt - Date.now()) / 60_000));
    return `${minutes} min`;
  })();

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex flex-col animate-fade-in"
      data-testid="full-player-screen"
      style={{
        background: 'linear-gradient(180deg, #173F35 0%, #0D2B24 100%)',
        color: '#F6F1E7',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-6 pb-3">
        <button
          onClick={onClose}
          data-testid="full-player-close"
          aria-label="Close player"
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <ChevronDown className="w-5 h-5" />
        </button>
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#C79A4A' }}>
          Now playing
        </p>
        <div className="w-10 h-10" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-4">
        {/* Artwork */}
        <div
          className="w-full max-w-[320px] aspect-square rounded-3xl overflow-hidden shadow-2xl mb-8"
          style={{ background: 'rgba(214,173,96,0.14)' }}
        >
          {current.artwork ? (
            <img src={current.artwork} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Volume2 className="w-16 h-16" style={{ color: '#C79A4A' }} />
            </div>
          )}
        </div>

        {/* Title + subtitle */}
        <div className="text-center mb-6 px-2 w-full max-w-[360px]">
          <p
            className="text-2xl leading-tight mb-1 truncate"
            style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}
            data-testid="full-player-title"
          >
            {current.title || 'Reflection'}
          </p>
          <p className="text-[13px]" style={{ color: 'rgba(246,241,231,0.65)' }}>
            {current.subtitle || 'Rowdy Solomon'}
          </p>
          {error && <p className="text-[12px] mt-1" style={{ color: '#E4A79E' }}>{error}</p>}
        </div>

        {/* Seek bar */}
        <div className="w-full max-w-[360px] mb-6">
          <div
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={position}
            className="relative h-1.5 bg-white/15 rounded-full cursor-pointer"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const ratio = (e.clientX - rect.left) / rect.width;
              seek(ratio * (duration || 0));
            }}
            data-testid="full-player-seek"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-150"
              style={{ width: `${pct}%`, background: '#C79A4A' }}
            />
          </div>
          <div className="flex justify-between mt-2 text-[11px]" style={{ color: 'rgba(246,241,231,0.65)' }}>
            <span>{fmt(position)}</span>
            <span>{fmt(duration)}</span>
          </div>
        </div>

        {/* Primary controls */}
        <div className="flex items-center justify-center gap-6 mb-8">
          <button
            onClick={() => skip(-15)}
            className="w-14 h-14 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors active:scale-95"
            data-testid="full-player-back-15"
            aria-label="Back 15 seconds"
          >
            <Rewind className="w-6 h-6" />
          </button>
          <button
            onClick={toggle}
            data-testid="full-player-toggle"
            aria-label={playing ? 'Pause' : 'Play'}
            aria-pressed={playing}
            className="w-20 h-20 rounded-full flex items-center justify-center transition-transform active:scale-95 shadow-2xl"
            style={{ background: '#C79A4A' }}
          >
            {loading ? (
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0D2B24' }} />
            ) : playing ? (
              <Pause className="w-8 h-8" style={{ color: '#0D2B24' }} />
            ) : (
              <Play className="w-8 h-8 ml-1" style={{ color: '#0D2B24' }} />
            )}
          </button>
          <button
            onClick={() => skip(15)}
            className="w-14 h-14 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors active:scale-95"
            data-testid="full-player-forward-15"
            aria-label="Forward 15 seconds"
          >
            <FastForward className="w-6 h-6" />
          </button>
        </div>

        {/* Content-aware secondary controls */}
        <div className="flex items-center justify-center gap-2 w-full max-w-[360px]">
          {!isSoundscape && (
            <button
              onClick={() => setShowSpeed(true)}
              data-testid="full-player-speed"
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-semibold hover:bg-white/10 transition-colors border border-white/20"
            >
              <Gauge className="w-4 h-4" />
              {rate === 1 ? 'Speed' : `${rate}x`}
            </button>
          )}
          <button
            onClick={() => setShowSleep(true)}
            data-testid="full-player-sleep-timer"
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-semibold hover:bg-white/10 transition-colors border"
            style={{ borderColor: sleepTimer ? '#C79A4A' : 'rgba(255,255,255,0.2)', color: sleepTimer ? '#C79A4A' : '#F6F1E7' }}
          >
            <Timer className="w-4 h-4" />
            {sleepTimer ? `Sleep · ${sleepRemainingLabel}` : 'Sleep timer'}
          </button>
        </div>
        <p className="text-[10px] mt-3" style={{ color: 'rgba(246,241,231,0.4)' }}>
          {tickTock >= 0 ? '' : ''}Audio continues when you close this screen.
        </p>
      </div>

      {/* Speed sheet */}
      {showSpeed && (
        <SheetPanel title="Playback speed" onClose={() => setShowSpeed(false)}>
          {SPEED_OPTIONS.map((s) => (
            <SheetRow
              key={s}
              selected={rate === s}
              onClick={() => { setRate(s); setShowSpeed(false); }}
              testid={`full-player-speed-${s}`}
            >
              {s}x{s === 1 && ' · normal'}
            </SheetRow>
          ))}
        </SheetPanel>
      )}

      {/* Sleep sheet */}
      {showSleep && (
        <SheetPanel title="Sleep timer" onClose={() => setShowSleep(false)}>
          <SheetRow
            selected={!sleepTimer}
            onClick={() => { setSleepTimer(null); setShowSleep(false); }}
            testid="full-player-sleep-off"
          >
            Off
          </SheetRow>
          {SLEEP_OPTIONS.map((o) => {
            const selected = sleepTimer?.endsAt === o.key || (typeof sleepTimer?.minutes === 'number' && sleepTimer.minutes === o.key);
            return (
              <SheetRow
                key={o.key}
                selected={!!selected}
                onClick={() => { setSleepTimer(o.key); setShowSleep(false); }}
                testid={`full-player-sleep-${o.key}`}
              >
                {o.label}
              </SheetRow>
            );
          })}
        </SheetPanel>
      )}
    </div>,
    document.body
  );
};

const SheetPanel = ({ title, onClose, children }) => (
  <div className="absolute inset-0 z-10 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
    <div
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-[420px] rounded-t-3xl sm:rounded-3xl overflow-hidden animate-slide-up"
      style={{ background: '#FFFCF6', color: '#252A28' }}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
        <p className="text-[12px] uppercase tracking-[0.2em] font-semibold" style={{ color: '#C79A4A' }}>{title}</p>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center">
          <X className="w-4 h-4 text-stone-500" />
        </button>
      </div>
      <div className="py-1 pb-4">{children}</div>
    </div>
  </div>
);

const SheetRow = ({ selected, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full flex items-center justify-between px-5 py-3 text-[15px] hover:bg-stone-50 transition-colors ${selected ? 'text-stone-800 font-semibold' : 'text-stone-700'}`}
  >
    <span>{children}</span>
    {selected && <Check className="w-4 h-4" style={{ color: '#C79A4A' }} />}
  </button>
);

export default FullPlayerScreen;
